import React, { useState } from 'react';
import { BrainCircuit, Loader2, Mail, Lock, User as UserIcon, LogIn, UserPlus, AlertCircle, Settings, ChevronDown, Copy, Globe, ArrowRight, AlertTriangle, KeyRound, CheckCircle2, ArrowLeft } from 'lucide-react';
import { User, Language } from '../types';
import { signInWithGoogle, loginWithEmail, registerWithEmail, resetPassword } from '../services/authService';
import { createOnboardingNotebooks } from '../utils/onboardingService';
import posthog from 'posthog-js';

interface LoginScreenProps {
    onLogin: (user: User) => void;
    clientId: string; // Deprecated but kept for type compatibility
    onOpenSettings: () => void;
    language?: Language;
    onToggleLanguage?: () => void;
    appVersion?: string;
    sessionExpired?: boolean;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({
    onLogin, onOpenSettings, language = 'english', onToggleLanguage, appVersion, sessionExpired = false
}) => {
    const [isLogin, setIsLogin] = useState(true);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [unauthorizedDomain, setUnauthorizedDomain] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showManualLogin, setShowManualLogin] = useState(false);


    // Manual Form State
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [resetEmail, setResetEmail] = useState('');

    const handleGoogleLogin = async () => {
        setIsLoading(true);
        setError(null);
        setUnauthorizedDomain(null);
        try {
            const user = await signInWithGoogle();
            if (user.isNewUser) {
                await createOnboardingNotebooks(user.id);
                posthog.capture('user_signed_up', { method: 'google' });
            }
            onLogin(user);
        } catch (err: any) {
            console.error("Google Login Detailed Error:", err);
            let msg = err.message || "Google Sign In Failed";

            if (err.code === 'auth/popup-closed-by-user') {
                msg = language === 'vietnamese' ? "Bạn đã đóng cửa sổ đăng nhập." : "Sign-in popup was closed.";
            } else if (err.code === 'auth/unauthorized-domain') {
                const currentLink = window.location.hostname;
                setUnauthorizedDomain(currentLink);
                msg = language === 'vietnamese'
                    ? `Tên miền "${currentLink}" chưa được cấp phép.`
                    : `Domain "${currentLink}" is not authorized.`;
            } else if (err.code === 'auth/popup-blocked') {
                msg = language === 'vietnamese'
                    ? "Trình duyệt đã chặn cửa sổ bật lên (Popup)."
                    : "Popup was blocked by the browser.";
            }

            setError(msg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleGuestLogin = () => {
        setIsLoading(true);
        setTimeout(() => {
            const guestUser: User = {
                id: 'guest',
                name: language === 'vietnamese' ? 'Khách' : 'Guest User',
                email: '',
                photoURL: undefined
            };
            onLogin(guestUser);
            setIsLoading(false);
        }, 500);
    };

    const handleAuth = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            let user;
            if (isLogin) {
                user = await loginWithEmail(email, password);
            } else {
                if (!name) {
                    throw new Error("Name is required");
                }
                user = await registerWithEmail(name, email, password);
                await createOnboardingNotebooks(user.id);
                posthog.capture('user_signed_up', { method: 'email' });
            }
            onLogin(user);
        } catch (err: any) {
            let msg = "Authentication failed";
            if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') msg = "Invalid email or password";
            if (err.code === 'auth/wrong-password') msg = "Incorrect password";
            if (err.code === 'auth/email-already-in-use') msg = "Email already in use";
            if (err.code === 'auth/weak-password') msg = "Password should be at least 6 characters";
            if (err.code === 'auth/operation-not-allowed') {
                msg = language === 'vietnamese'
                    ? "Chưa bật đăng nhập Email/Password trong Firebase Console."
                    : "Email/Password provider is not enabled in Firebase Console.";
            }
            setError(msg);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordReset = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccessMessage(null);
        setIsLoading(true);

        if (!resetEmail) {
            setError(language === 'vietnamese' ? 'Vui lòng nhập email.' : 'Please enter your email.');
            setIsLoading(false);
            return;
        }

        try {
            await resetPassword(resetEmail);
            setSuccessMessage(language === 'vietnamese'
                ? 'Nếu tài khoản tồn tại, email khôi phục đã được gửi. Vui lòng kiểm tra hộp thư (cả mục Spam).'
                : 'If an account exists, a reset link has been sent. Please check your inbox and spam folder.');
        } catch (err: any) {
            console.error("Password reset error", err);
            let msg = "Failed to send reset email";

            // Detailed error mapping for troubleshooting
            if (err.code === 'auth/user-not-found') msg = "No account found with this email.";
            else if (err.code === 'auth/invalid-email') msg = "Invalid email format.";
            else if (err.code === 'auth/too-many-requests') msg = "Too many attempts. Please try again later.";
            else if (err.code === 'auth/operation-not-allowed') msg = "Configuration Error: Email/Password sign-in is NOT enabled in Firebase Console.";
            else if (err.code === 'auth/unauthorized-continue-uri') msg = "Configuration Error: Domain not authorized in Firebase Console.";
            else if (err.message) msg = `Error: ${err.message}`;

            setError(msg);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen w-full bg-gray-50 dark:bg-slate-900 flex flex-col items-center justify-center p-4 pattern-grid relative overflow-hidden">
            {/* Background Decor */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-500/10 rounded-full blur-[100px]" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-500/10 rounded-full blur-[100px]" />
            </div>

            <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-3xl shadow-2xl border border-gray-100 dark:border-slate-700 overflow-hidden relative z-10 animate-in fade-in zoom-in-95 duration-500">
                <div className="p-8">

                    {/* Header */}
                    <div className="text-center mb-6 relative">
                        <div className="absolute top-0 right-0 flex gap-2">
                            {onToggleLanguage && (
                                <button
                                    data-testid="language-toggle-btn"
                                    onClick={onToggleLanguage}
                                    className="min-h-[44px] min-w-[44px] p-2 text-gray-400 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition-colors flex items-center justify-center gap-1"
                                    title={language === 'english' ? "Switch to Vietnamese" : "Chuyển sang Tiếng Anh"}
                                >
                                    <Globe size={18} />
                                    <span className="text-[10px] font-bold uppercase">{language === 'english' ? 'EN' : 'VN'}</span>
                                </button>
                            )}
                        </div>

                        {sessionExpired && (
                            <div className="mb-6 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl flex items-start gap-3">
                                <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
                                <div className="text-left">
                                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                                        {language === 'vietnamese' ? 'Phiên làm việc đã hết hạn' : 'Session expired'}
                                    </p>
                                    <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5 leading-snug">
                                        {language === 'vietnamese'
                                            ? 'Đăng nhập lại để tiếp tục — dữ liệu của bạn vẫn được lưu an toàn.'
                                            : 'Sign in again to continue — your data is safely saved.'}
                                    </p>
                                </div>
                            </div>
                        )}
                        <div className="w-20 h-20 mx-auto mb-6">
                            <img src="/logo-mark.png" alt="StudyBoard" className="w-full h-full object-contain" />
                        </div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">StudyBoard</h1>
                        <p className="text-gray-500 dark:text-gray-400 text-sm font-medium tracking-wide">
                            Think · Explain · Master
                        </p>
                        <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
                            {language === 'vietnamese' ? 'AI system biến ghi chú thành hiểu biết thật sự.' : 'An AI system that turns notes into understanding.'}
                        </p>
                    </div>

                    {showForgotPassword ? (
                        // FORGOT PASSWORD VIEW
                        <div className="animate-in slide-in-from-right-4 fade-in duration-300">
                            <div className="mb-6 text-center">
                                <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-2">
                                    {language === 'vietnamese' ? 'Khôi phục mật khẩu' : 'Reset Password'}
                                </h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {language === 'vietnamese'
                                        ? 'Nhập email của bạn để nhận liên kết đặt lại mật khẩu.'
                                        : 'Enter your email to receive a password reset link.'}
                                </p>
                            </div>

                            <form onSubmit={handlePasswordReset} className="space-y-4">
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase ml-1">Email</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                        <input
                                            data-testid="reset-email-input"
                                            type="email"
                                            value={resetEmail}
                                            onChange={(e) => setResetEmail(e.target.value)}
                                            className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
                                            placeholder="you@example.com"
                                        />
                                    </div>
                                </div>

                                {error && (
                                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 rounded-xl flex items-center gap-2 text-sm text-red-600 dark:text-red-400 animate-in fade-in slide-in-from-top-1 break-words">
                                        <AlertCircle size={16} className="flex-shrink-0" />
                                        <span className="leading-tight">{error}</span>
                                    </div>
                                )}

                                {successMessage && (
                                    <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900/30 rounded-xl flex items-center gap-2 text-sm text-green-600 dark:text-green-400 animate-in fade-in slide-in-from-top-1 break-words">
                                        <CheckCircle2 size={16} className="flex-shrink-0" />
                                        <span className="leading-tight">{successMessage}</span>
                                    </div>
                                )}

                                <button
                                    data-testid="reset-submit-btn"
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 shadow-lg mt-2"
                                >
                                    {isLoading ? (
                                        <Loader2 size={20} className="animate-spin" />
                                    ) : (
                                        <>
                                            <KeyRound size={20} /> {language === 'vietnamese' ? 'Gửi liên kết' : 'Send Reset Link'}
                                        </>
                                    )}
                                </button>
                            </form>

                            <button
                                onClick={() => {
                                    setShowForgotPassword(false);
                                    setError(null);
                                    setSuccessMessage(null);
                                }}
                                className="w-full mt-4 py-3 text-sm font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-xl transition-colors flex items-center justify-center gap-2"
                            >
                                <ArrowLeft size={16} /> {language === 'vietnamese' ? 'Quay lại đăng nhập' : 'Back to Login'}
                            </button>
                        </div>
                    ) : (
                        // MAIN LOGIN VIEW
                        <>

                            {/* Google Sign In Section - Primary */}
                            <div className="mb-4">
                                <button
                                    data-testid="google-login-btn"
                                    onClick={handleGoogleLogin}
                                    className="w-full py-3 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl font-semibold text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors flex items-center justify-center gap-3 relative"
                                >
                                    <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" />
                                    <span>{language === 'vietnamese' ? 'Tiếp tục với Google' : 'Continue with Google'}</span>
                                </button>
                            </div>

                            {unauthorizedDomain && (
                                <div className="mb-4 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700/50 rounded-xl animate-in fade-in slide-in-from-top-1">
                                    <div className="flex items-start gap-2 text-orange-800 dark:text-orange-200 mb-2">
                                        <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                                        <span className="font-bold text-sm">
                                            {language === 'vietnamese' ? 'Cấu hình Firebase chưa hoàn tất' : 'Firebase Configuration Required'}
                                        </span>
                                    </div>
                                    <p className="text-xs text-orange-700 dark:text-orange-300 mb-3 leading-relaxed">
                                        {language === 'vietnamese'
                                            ? 'Vui lòng thêm tên miền này vào danh sách Authorized Domains trong Firebase Console:'
                                            : 'Please add this domain to the Authorized Domains list in your Firebase Console:'}
                                    </p>
                                    <div className="flex items-center gap-2 bg-white dark:bg-slate-900 border border-orange-200 dark:border-orange-800 rounded-lg p-2">
                                        <code className="flex-1 text-xs font-mono text-gray-600 dark:text-gray-300 truncate select-all">
                                            {unauthorizedDomain}
                                        </code>
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(unauthorizedDomain);
                                                // Use minimal inline feedback
                                                const btn = document.activeElement as HTMLElement;
                                                if (btn) {
                                                    const original = btn.innerHTML;
                                                    btn.innerHTML = language === 'vietnamese' ? 'OK' : 'OK';
                                                    setTimeout(() => btn.innerHTML = original, 1000);
                                                }
                                            }}
                                            className="p-1.5 hover:bg-orange-100 dark:hover:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-md transition-colors"
                                            title="Copy"
                                        >
                                            <Copy size={14} />
                                        </button>
                                    </div>
                                    <div className="mt-3 text-[10px] text-orange-600/80 dark:text-orange-400/80">
                                        Firebase Console &rarr; Authentication &rarr; Settings &rarr; Authorized Domains
                                    </div>
                                </div>
                            )}

                            {error && !unauthorizedDomain && !showManualLogin && (
                                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 rounded-xl flex items-start gap-2 text-sm text-red-600 dark:text-red-400 animate-in fade-in slide-in-from-top-1 break-words">
                                    <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                                    <span className="whitespace-pre-wrap font-medium">{error}</span>
                                </div>
                            )}

                            {/* Manual Login - Collapsible */}
                            {!showManualLogin ? (
                                <button
                                    data-testid="manual-login-toggle"
                                    onClick={() => setShowManualLogin(true)}
                                    className="w-full py-3 border border-gray-200 dark:border-slate-700 rounded-xl font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors flex items-center justify-center gap-2"
                                >
                                    <Mail size={18} /> {language === 'vietnamese' ? 'Email & Mật khẩu' : 'Email & Password'}
                                    <ChevronDown size={16} />
                                </button>
                            ) : (
                                <div className="animate-in slide-in-from-top-2 fade-in">
                                    <form onSubmit={handleAuth} className="space-y-4">
                                        {!isLogin && (
                                            <div className="space-y-1">
                                                <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase ml-1">
                                                    {language === 'vietnamese' ? 'Họ tên' : 'Full Name'}
                                                </label>
                                                <div className="relative">
                                                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                                    <input
                                                        data-testid="auth-name-input"
                                                        type="text"
                                                        value={name}
                                                        onChange={(e) => setName(e.target.value)}
                                                        className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
                                                        placeholder="John Doe"
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase ml-1">Email</label>
                                            <div className="relative">
                                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                                <input
                                                    data-testid="auth-email-input"
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
                                                    placeholder="you@example.com"
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-1">
                                            <div className="flex justify-between items-center px-1">
                                                <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">
                                                    {language === 'vietnamese' ? 'Mật khẩu' : 'Password'}
                                                </label>
                                                {isLogin && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setResetEmail(email); // Pre-fill with login email
                                                            setShowForgotPassword(true);
                                                            setError(null);
                                                            setSuccessMessage(null);
                                                        }}
                                                        className="min-h-[44px] px-2 flex items-center text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                                    >
                                                        {language === 'vietnamese' ? 'Quên mật khẩu?' : 'Forgot Password?'}
                                                    </button>
                                                )}
                                            </div>
                                            <div className="relative">
                                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                                <input
                                                    data-testid="auth-password-input"
                                                    type="password"
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
                                                    placeholder="••••••••"
                                                />
                                            </div>
                                        </div>

                                        {error && (
                                            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 rounded-xl flex items-center gap-2 text-sm text-red-600 dark:text-red-400 animate-in fade-in slide-in-from-top-1 break-words">
                                                <AlertCircle size={16} className="flex-shrink-0" />
                                                <span>{error}</span>
                                            </div>
                                        )}

                                        <button
                                            data-testid="auth-submit-btn"
                                            type="submit"
                                            disabled={isLoading}
                                            className="w-full py-3 px-4 bg-gray-900 dark:bg-blue-600 hover:opacity-90 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 shadow-lg mt-2"
                                        >
                                            {isLoading ? (
                                                <Loader2 size={20} className="animate-spin" />
                                            ) : isLogin ? (
                                                <>
                                                    <LogIn size={20} /> {language === 'vietnamese' ? 'Đăng nhập' : 'Sign In'}
                                                </>
                                            ) : (
                                                <>
                                                    <UserPlus size={20} /> {language === 'vietnamese' ? 'Tạo tài khoản' : 'Create Account'}
                                                </>
                                            )}
                                        </button>
                                    </form>

                                    <div className="mt-4 text-center">
                                        <button
                                            data-testid="auth-switch-mode-btn"
                                            onClick={() => { setIsLogin(!isLogin); setError(null); }}
                                            className="min-h-[44px] px-3 flex items-center justify-center text-sm text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 font-medium transition-colors"
                                        >
                                            {isLogin
                                                ? (language === 'vietnamese' ? "Chưa có tài khoản? Đăng ký ngay" : "Don't have an account? Sign Up")
                                                : (language === 'vietnamese' ? "Đã có tài khoản? Đăng nhập" : "Already have an account? Sign In")
                                            }
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Footer Info */}
            <div className="mt-6 flex flex-col items-center gap-2 z-10 max-w-lg w-full px-4">
                <a
                    href="/landing.html"
                    className="text-sm text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors font-medium inline-flex items-center gap-1"
                >
                    {language === 'vietnamese' ? 'StudyBoard là gì? →' : 'What is StudyBoard? →'}
                </a>
                <a
                    href="/privacy.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors"
                >
                    {language === 'vietnamese' ? 'Chính sách riêng tư' : 'Privacy Policy'}
                </a>
                {appVersion && (
                    <div className="text-[10px] text-gray-400 dark:text-gray-600 font-mono">
                        {appVersion}
                    </div>
                )}
            </div>

        </div>
    );
};